import type { QueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, InputNumber, List, Modal, Radio, Tag } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsCreateProduct } from '@/api/generated/endpoints/products/products';
import type { ProductCreate, ProductOut, ProductRef } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { problemMessage } from '@/i18n/problem';
import { applyProblem } from '@/lib/problemForm';
import { PRODUCT_FIELD_BY_CODE } from '@/pages/catalog/productForm';
import { invalidatePath } from '@/lib/queryCache';
import { uomLabel } from '@/lib/units';

interface Candidate {
  product: ProductRef;
  score: number;
}

export function ProductCreateModal({
  open,
  onClose,
  onCreated,
  queryClient,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (p: ProductOut) => void;
  queryClient: QueryClient;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<ProductCreate>();
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const create = useProductsCreateProduct({
    mutation: {
      onSuccess: async (p) => {
        await invalidatePath(queryClient, '/api/v1/products');
        form.resetFields();
        setCandidates(null);
        onCreated(p);
      },
      onError: (e) => {
        // Похожие товары — не ошибка, а вопрос: «может, это один из них?» (ADR-0005 п.2).
        if (e instanceof ApiProblem && e.code === 'similar_products') {
          setCandidates((e.extra.candidates as Candidate[] | undefined) ?? []);
          return;
        }
        if (!applyProblem(form, e, PRODUCT_FIELD_BY_CODE)) setError(problemMessage(e));
      },
    },
  });

  function submit(values: ProductCreate, confirmNew = false) {
    setError(null);
    create.mutate({ data: { ...values, confirm_new: confirmNew } });
  }

  return (
    <Modal
      title={t('products.new')}
      open={open}
      onCancel={() => {
        setCandidates(null);
        setError(null);
        onClose();
      }}
      okText={t('common.create')}
      okButtonProps={{ loading: create.isPending, disabled: candidates !== null }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<ProductCreate>
        form={form}
        layout="vertical"
        initialValues={{ base_uom: 'kg' }}
        onFinish={(v) => {
          submit(v);
        }}
        onValuesChange={() => {
          setCandidates(null);
        }}
      >
        <Form.Item
          name="name"
          label={t('products.name')}
          rules={[{ required: true, whitespace: true, message: t('products.nameRequired') }]}
        >
          <Input autoFocus maxLength={300} placeholder="Клубника" />
        </Form.Item>
        <Form.Item name="base_uom" label={t('products.baseUom')} extra={t('products.baseUomHint')}>
          <Radio.Group
            options={[
              { label: t('uom.kgLong'), value: 'kg' },
              { label: t('uom.pcsLong'), value: 'pcs' },
              { label: t('uom.boxLong'), value: 'box' },
            ]}
            optionType="button"
          />
        </Form.Item>
        <Form.Item
          name="shelf_life_days"
          label={t('products.shelfLife')}
          extra={t('products.shelfLifeHint')}
        >
          <InputNumber min={1} max={3650} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="sku" label={t('products.skuOptional')}>
          <Input maxLength={64} />
        </Form.Item>
      </Form>
      {candidates && (
        <Alert
          type="warning"
          showIcon
          message={t('products.similarTitle')}
          description={
            <>
              <List
                size="small"
                dataSource={candidates}
                renderItem={(c) => (
                  <List.Item>
                    {c.product.name} <Tag>{uomLabel(t, c.product.base_uom)}</Tag>
                  </List.Item>
                )}
              />
              <Button
                danger
                onClick={() => {
                  submit(form.getFieldsValue(), true);
                }}
                loading={create.isPending}
              >
                {t('products.createAnyway')}
              </Button>
            </>
          }
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
}
